import {
  QUESTION_IDS,
  SURVEY_QUESTIONS,
  type QuestionId,
  type SurveyKey,
  missingQuestionIds,
  type SurveyAnswers,
} from '../shared/survey';
import type { HvyBlock, HvyDocument } from './hvy-types';

export const DRAFT_STORAGE_KEY = 'petro-survey-draft-v1';

export interface SurveyDraft {
  surveyId?: string;
  surveyVersion: number;
  accessCode: string;
  answers: Partial<SurveyAnswers>;
  feedback: string;
  updatedAt: string;
}

export function walkBlocks(document: HvyDocument): HvyBlock[] {
  const found: HvyBlock[] = [];
  const visited = new WeakSet<object>();
  const visit = (value: unknown): void => {
    if (!value || typeof value !== 'object' || value instanceof Uint8Array || visited.has(value)) return;
    visited.add(value);
    if (isBlock(value)) found.push(value);
    Object.values(value).forEach(visit);
  };
  visit(document.sections);
  return found;
}

export function extractSurveyState(document: HvyDocument): { answers: Partial<SurveyAnswers>; feedback: string; accessCode: string } {
  const answers: Partial<SurveyAnswers> = {};
  let feedback = '';
  let accessCode = '';
  walkBlocks(document).forEach((block) => {
    const id = block.schema.id ?? '';
    if (id === 'additional-feedback') feedback = block.text.trim();
    if (id === 'survey-access-code') accessCode = String(block.schema.pluginConfig?.value ?? '');
    const match = /^(q\d{2})-answer/.exec(id);
    if (!match || !QUESTION_IDS.includes(match[1] as keyof SurveyAnswers)) return;
    const questionId = match[1] as keyof SurveyAnswers;
    const lines = block.text.split('\n');
    lines.forEach((line, index) => {
      const marker = /\([xX]\)(?:\s+(.*))?$/.exec(line);
      if (!marker) return;
      const candidate = (marker[1]?.trim() || nextAnswerLabel(lines, index + 1));
      const canonical = (SURVEY_QUESTIONS[questionId] as readonly string[]).find((option) => option.toLowerCase() === candidate.toLowerCase());
      if (canonical) answers[questionId] = canonical;
    });
  });
  return { answers, feedback, accessCode };
}

export function restoreSurveyState(document: HvyDocument, draft: SurveyDraft): void {
  walkBlocks(document).forEach((block) => {
    const id = block.schema.id ?? '';
    if (id === 'survey-access-code') {
      block.schema.pluginConfig = { ...(block.schema.pluginConfig ?? {}), value: draft.accessCode };
    }
    if (id === 'additional-feedback') block.text = draft.feedback;
    const match = /^(q\d{2})-answer/.exec(id);
    if (!match || !QUESTION_IDS.includes(match[1] as keyof SurveyAnswers)) return;
    const questionId = match[1] as keyof SurveyAnswers;
    const selected = draft.answers[questionId];
    const lines = block.text.split('\n');
    block.text = lines.map((line, index) => line.replace(/\(([ xX])\)(?:\s+(.*))?$/, (_whole, _state, inlineLabel: string | undefined) => {
      const label = inlineLabel?.trim() || nextAnswerLabel(lines, index + 1);
      const spacingAndLabel = inlineLabel === undefined ? '' : ` ${inlineLabel}`;
      return `(${selected?.toLowerCase() === label.toLowerCase() ? 'x' : ' '})${spacingAndLabel}`;
    })).join('\n');
  });
}

export function setSurveyAccessCode(document: HvyDocument, accessCode: string): void {
  const block = walkBlocks(document).find((candidate) => candidate.schema.id === 'survey-access-code');
  if (block) block.schema.pluginConfig = { ...(block.schema.pluginConfig ?? {}), value: accessCode };
}

export function setSurveyQuestionCount(document: HvyDocument, questionCount: number): void {
  walkBlocks(document).forEach((block) => {
    block.text = block.text.replace(/\b\d+[ -]question survey\b/gi, `${questionCount}-question survey`);
  });
}

export function loadDraft(storage: Storage = localStorage, surveyId = '', surveyVersion = 0, surveyKey?: SurveyKey): SurveyDraft | null {
  try {
    const value = JSON.parse(storage.getItem(draftStorageKey(surveyId, surveyKey)) ?? 'null') as Partial<SurveyDraft> | null;
    if (!value || (surveyVersion > 0 && value.surveyVersion !== surveyVersion) || typeof value.accessCode !== 'string'
      || (value.surveyId !== undefined && value.surveyId !== surveyId)
      || typeof value.feedback !== 'string' || !value.answers || typeof value.answers !== 'object') return null;
    return value as SurveyDraft;
  } catch {
    return null;
  }
}

export function saveDraft(document: HvyDocument, storage: Storage = localStorage, surveyId = '', surveyVersion = 0, surveyKey?: SurveyKey): void {
  const state = extractSurveyState(document);
  const draft: SurveyDraft = {
    surveyId,
    surveyVersion,
    accessCode: state.accessCode,
    answers: state.answers,
    feedback: state.feedback,
    updatedAt: new Date().toISOString(),
  };
  storage.setItem(draftStorageKey(surveyId, surveyKey), JSON.stringify(draft));
}

export function clearDraft(storage: Storage = localStorage, surveyId = '', surveyKey?: SurveyKey): void {
  storage.removeItem(draftStorageKey(surveyId, surveyKey));
}

export function getMissingAnswers(document: HvyDocument, questionIds?: readonly QuestionId[]) {
  return missingQuestionIds(extractSurveyState(document).answers, questionIds);
}

function draftStorageKey(surveyId: string, surveyKey?: SurveyKey): string {
  if (surveyKey === 'original' || (!surveyKey && !surveyId)) return DRAFT_STORAGE_KEY;
  return `${DRAFT_STORAGE_KEY}:${surveyKey ?? surveyId}`;
}

function isBlock(value: object): value is HvyBlock {
  const candidate = value as Partial<HvyBlock>;
  return typeof candidate.text === 'string' && Boolean(candidate.schema) && typeof candidate.schema === 'object';
}

function nextAnswerLabel(lines: string[], start: number): string {
  for (let index = start; index < lines.length; index += 1) {
    const candidate = lines[index]?.trim() ?? '';
    if (candidate && !candidate.startsWith('<!--hvy:')) return candidate;
  }
  return '';
}
